import classnames from 'classnames'
import styles from './ProductGallery.module.scss'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import ProductGalleryGridView from '@/sections/ProductGallery/ProductGalleryGridView/ProductGalleryGridView'
import ProductGalleryListView from '@/sections/ProductGallery/ProductGalleryListView/ProductGalleryListView'
import { ScrollContext } from '@/context/Scroll'
import gsap from 'gsap'
import useWindowResize from '@/hooks/use-window-resize'
import { Flip } from 'gsap/dist/Flip'
import { wait } from '@/utils'
import useStore from '@/store'
import SanityImage from '@/components/SanityImage/SanityImage'
import useCurrentPage from '@/hooks/use-current-page'
import { COLORS } from '@/sections/HomeProductList/HomeProductPanningContainer/HomeProductPanningContainer.helper'
import { TRANSITION_DURATION, TRANSITION_DURATION_MS } from '@/data'
import { getIsMobile } from '@/hooks/use-breakpoint'

gsap.registerPlugin(Flip)

// const MIN_PRODUCTS = 49

export const getGalleryTopOffset = () => {
  if (getIsMobile(window.innerWidth)) {
    return window.innerWidth * 0.35
  } else {
    return window.innerWidth * 0.15
  }
}

export const PRODUCT_GALLERY_BREAKPOINTS = {
  tablet: {
    width: 800,
  },
  xs: {
    width: 600,
  },
}

const ProductGallery = ({ className, products, filters }) => {
  const { onScrollCallback, scroll } = useContext(ScrollContext)
  const productGalleryType = useStore(state => state.productGalleryType)
  const $inner = useRef(null)
  const gridViewRef = useRef(null)
  const listViewRef = useRef(null)
  const $container = useRef(null)
  const initialAnimationDebounce = useRef(null)
  const $transitionBoxes = useRef([])
  const $transitionBoxesMasterContainer = useRef(null)
  const $transitionBoxesMasterInner = useRef(null)
  const $views = useRef(null)
  const animatedIn = useRef(false)
  const resizeKey = useWindowResize()
  const scrollStartDebounce = useRef(null)
  const [galleryTopOffset, setGalleryTopOffset] = useState(0)
  const transitionCount = useRef(0)
  const setProductGalleryTransitioning = useStore(state => state.setProductGalleryTransitioning)
  const productGalleryActiveFilter = useStore(state => state.productGalleryActiveFilter)
  const setProductGalleryProducts = useStore(state => state.setProductGalleryProducts)
  const setProductGalleryFilters = useStore(state => state.setProductGalleryFilters)
  const { currentPath } = useCurrentPage()
  const setProductGalleryType = useStore(state => state.setProductGalleryType)
  const [pageIsLeaving, setPageIsLeaving] = useState(false)
  const [allowListRender, setAllowListRender] = useState(false)
  const prevPath = useStore(state => state.prevPath)
  const prevPathRef = useRef(currentPath)
  const loaderAnimationComplete = useStore(state => state.loaderAnimationComplete)
  // const setDebugMobileText = useStore(state => state.setDebugMobileText)

  // products = useMemo(() => {
  //   if (products.length < MIN_PRODUCTS) {
  //     const formatted = []
  //     for (let index = 0; index < MIN_PRODUCTS; index++) {
  //       const modulo = index % products.length
  //       const element = products[modulo]
  //       formatted.push(element)
  //     }
  //     return formatted
  //   }

  //   return products
  // }, [products])

  const colorsByIndex = useMemo(() => {
    const colors = []
    const colorValues = Object.values(COLORS)
    products.forEach((_, i) => {
      const colorIndex = i % colorValues.length
      colors[i] = colorValues[colorIndex]
    })
    return colors
  }, [products])

  const offsets = useRef({
    grid: [],
    list: [],
  })

  const $outerContainerElements = useRef({
    grid: null,
    list: null,
  })

  const viewHeights = useRef({
    grid: 0,
    list: 0,
  })

  useEffect(() => {
    setGalleryTopOffset(getGalleryTopOffset())
    setProductGalleryProducts(products)
    setProductGalleryFilters(filters)
  }, [resizeKey, products, setProductGalleryProducts, filters, setProductGalleryFilters])

  const animateBoxes = async (currentView, scroll) => {
    if (transitionCount.current === 0) {
      transitionCount.current++
      return
    }

    setProductGalleryTransitioning(true)

    listViewRef.current.raf() // need single tick to detect height/width

    const gridOffset = gridViewRef.current.getOffsetByPercentScrolled(scroll.progress)
    const listOffset = listViewRef.current.getOffsetByPercentScrolled(scroll.progress)
    const offsetToScroll = currentView === 'list' ? listOffset : gridOffset

    const offsetsSnapshot = { ...offsets.current }

    scroll.resize()

    scroll.scrollTo(offsetToScroll, {
      immediate: true,
    })

    scroll.stop()

    const $gridView = gridViewRef.current.getElement()
    const $listView = listViewRef.current.getElement()

    viewHeights.current.grid = $gridView.current.offsetHeight
    viewHeights.current.list = $listView.current.offsetHeight
    const activeHeight = productGalleryType === 'grid' ? viewHeights.current.grid : viewHeights.current.list

    if (currentView === 'list') {
      listViewRef.current.animateLerpValue(false)
    }

    const transitionBoxes = () => {
      const setPositions = () => {
        $transitionBoxes.current.forEach(($el, i) => {
          if (!$el) return
          const offset = offsetsSnapshot[productGalleryType === 'grid' ? 'list' : 'grid'][i]
          if (!offset) return

          gsap.set($el, {
            y: offset.top,
            x: offset.left,
            width: offset.width,
            height: offset.height,
          })
        })

        let top = offsetToScroll
        if (window.innerHeight + top > activeHeight) {
          top = activeHeight - window.innerHeight
        }
        gsap.set($transitionBoxesMasterContainer.current, {
          top,
          opacity: 1,
        })
      }

      const reset = () => {
        gsap.set($transitionBoxesMasterContainer.current, {
          opacity: 0.00001,
        })
      }

      return {
        setPositions,
        reset,
      }
    }

    transitionBoxes().setPositions()

    const $listViewContainer = listViewRef.current.getElement().current
    const $gridViewContainer = gridViewRef.current.getElement().current
    const $listBoxes = $listViewContainer.querySelectorAll('[data-box-type="list"]')
    const $gridBoxes = $gridViewContainer.querySelectorAll('[data-box-type="grid"]')
    let boxesToAnimate = currentView === 'list' ? $listBoxes : $gridBoxes
    boxesToAnimate = Array.from(boxesToAnimate)

    boxesToAnimate.forEach(async ($el, i) => {
      listViewRef.current.raf() // need single tick to detect height/width
      const $elTiedTo = $el.dataset.index ? $transitionBoxes.current[i] : null
      if (!$el || !$elTiedTo) return

      const animateFromBoxState = Flip.getState($el)

      gsap.killTweensOf($el)

      Flip.fit($el, $elTiedTo, {
        scale: true,
      })

      await wait(10)

      transitionBoxes().reset()

      await wait(10)

      Flip.to(animateFromBoxState, {
        duration: 1,
        scale: true,
        ease: 'power3.inOut',
        onComplete: () => {
          gsap.set($el, {
            clearProps: 'all',
          })

          if (currentView === 'grid') {
            listViewRef.current.animateLerpValue(true)
          }

          setTimeout(() => {
            offsets.current.grid = gridViewRef.current.calculateOffsetOnScroll()
            offsets.current.list = listViewRef.current.calculateOffsetOnScroll()
            setProductGalleryTransitioning(false)
          }, 5)
        },
      })

      return
    })

    scrollStartDebounce.current = setTimeout(() => {
      scroll.start()
      scroll.resize()
    }, 50)
  }

  useEffect(() => {
    if (!onScrollCallback) return

    const key = 'listView'

    onScrollCallback({
      key,
      remove: true,
    })

    onScrollCallback({
      key,
      callback: e => {
        if (!gridViewRef.current || !listViewRef.current) return

        offsets.current.grid = gridViewRef.current.calculateOffsetOnScroll()
        offsets.current.list = listViewRef.current.calculateOffsetOnScroll()
        listViewRef.current.getActiveProductDataByPercentScrolled(e.progress)
      },
    })

    return () => {
      onScrollCallback({
        key,
        remove: true,
      })
    }
  }, [onScrollCallback])

  useEffect(() => {
    if (!scroll) return
    if (!allowListRender) return

    if (prevPathRef.current !== currentPath) {
      scroll.stop()
      setPageIsLeaving(true)
      return
    }

    if (!gridViewRef.current) return
    animateBoxes(productGalleryType, scroll, currentPath)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productGalleryType, scroll, resizeKey, allowListRender])

  useEffect(() => {
    return () => {
      setProductGalleryType('grid')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!loaderAnimationComplete) return
    if (!$container.current || animatedIn.current) return
    if (initialAnimationDebounce.current) {
      clearTimeout(initialAnimationDebounce.current)
    }
    initialAnimationDebounce.current = setTimeout(
      () => {
        const duration = TRANSITION_DURATION * 0.7
        gsap.fromTo(
          $container.current,
          {
            opacity: 0,
            y: window.innerHeight * 0.25,
          },
          {
            opacity: 1,
            y: 0,
            duration,
            ease: 'Power3.easeOut',
            onComplete: () => {
              animatedIn.current = true
            },
          },
        )

        setTimeout(
          () => {
            setAllowListRender(true)
          },
          duration * 0.5 * 1000,
        )
      },
      !prevPath ? 50 : TRANSITION_DURATION_MS * 0.5,
    )
  }, [resizeKey, prevPath, loaderAnimationComplete])

  if (!products?.length) return null

  return (
    <div
      className={classnames(styles.ProductGallery, className)}
      ref={$container}
      data-view={productGalleryType}
      style={{
        '--gallery-top-offset': `${galleryTopOffset}px`,
      }}
    >
      <div
        className={styles.transitionBoxes}
        ref={$transitionBoxesMasterContainer}
      >
        <div
          className={styles.transitionBoxesInner}
          ref={$transitionBoxesMasterInner}
        >
          {products.map((product, i) => {
            return (
              <div
                key={i}
                data-index={i}
                ref={ref => {
                  $transitionBoxes.current[i] = ref
                }}
                className={classnames(styles.transitionBox, {
                  [styles.notInFilter]:
                    productGalleryActiveFilter && productGalleryActiveFilter !== product.productCategory.title,
                })}
              >
                <SanityImage
                  image={products[i].image}
                  className={styles.transitionBox__image}
                  breakpoints={PRODUCT_GALLERY_BREAKPOINTS}
                />
              </div>
            )
          })}
        </div>
      </div>
      <div
        className={styles.inner}
        ref={$inner}
      >
        <div
          className={styles.views}
          ref={$views}
        >
          <div
            className={styles.listViewContainer}
            ref={ref => {
              $outerContainerElements.current.list = ref
            }}
          >
            <ProductGalleryListView
              products={products}
              className={styles.listView}
              ref={listViewRef}
              pageIsLeaving={pageIsLeaving}
              allowListRender={allowListRender}
            />
          </div>
          <div
            className={styles.gridViewContainer}
            ref={ref => {
              $outerContainerElements.current.grid = ref
            }}
          >
            <ProductGalleryGridView
              products={products}
              className={styles.gridView}
              ref={gridViewRef}
              colorsByIndex={colorsByIndex}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

ProductGallery.displayName = 'ProductGallery'

export default ProductGallery
